// TODO cleaner, more obvious data model (e.g. no separate get* method & getter, or make 1 private)

/************************************** THIRD-PARTY MODULES ***************************************/
import {observable, action, computed, isObservableArray, IObservableArray} from 'mobx';
import {Int} from 'mad-utils/lib/shared';
import {get} from 'mad-utils/lib/react';

import {RecordInstance, NotificationInstance} from '@canimmunize/javelinscript';

/**************************************** PROJECT MODULES *****************************************/
import {RootStore} from '../root-store';
import {RecordModel} from './record-model';

/**************************************** TYPE DEFINITIONS ****************************************/
type RecordId = string;

/********************************************* EXPORT *********************************************/
/**
 * Store record models
 */
export class RecordModelsStore {
    public static Instance: RecordModelsStore | null = null;

    /**
     * All stored record models
     */
    @observable
    private _recordModels: IObservableArray<RecordModel> = [] as any;

    /****************** MAIN GETTERS *******************/
    /**
     * Build record models from records & return them
     * @return {RecordModel[]} Collection of all RecordModel objects. Each represents an entry in
     *                         the My Records menu & corresponding data shown when it's selected
     */
    public get recordModels(): IObservableArray<RecordModel> {
        if (
            (!this._recordModels || this._recordModels.length === 0) &&
            (!this.rootStore.records || this.rootStore.records.length === 0)
        ) {
            /**
             * (1) CONDITION :: No record models, no record models available
             */
            return observable([]);
        } else if (!this._recordModels || this._recordModels.length === 0) {
            /**
             * (2) CONDITION :: No record models (build all record models from scratch)
             */
            const recModels = this.rootStore.records.map(this.buildRecordModel);
            this.setRecordModels(recModels);
            return this._recordModels;
        } else if (this._recordModels.length === this.rootStore.records.length) {
            /**
             * (3) CONDITION: no new/rm records (# recordModels = # records); ret existing records
             *   |--> Should autorerun any time we + or - a record, so we shouldn't see cases where
             *        1 record is added & another rm (thus triggering this condition erroneously)
             */
            return this._recordModels;
        } else if (this._recordModels.length < this.rootStore.records.length) {
            /**
             * (4) CONDITION :: Added record (If there are more records than record models)
             */
            return this.handleAddedRecords();
        } else if (this.rootStore.records.length < this._recordModels.length) {
            /**
             * (5) CONDITION :: Deleted record (fewer records than record models present)
             */
            return this.handleDeletedRecords();
        }

        // Failsafe - return
        return this._recordModels;
    }

    /**
     * @return {boolean} true if record with given ID exists
     */
    public hasRecord = (recordId: string) =>
        !!this.recordModels.find(recordModel => recordModel.recordId === recordId);

    /************ ADD RECORD MODELS ************/
    /**
     * Build then store RecordModel if given record; if given RecordModel, store it as-is
     * @param {Object} record Record object to build RecordModel from, or existing RecordModel
     * @return {RecordModel} Given RecordModel, or RecordModel built from given record
     */
    @action
    public addRecordModel = (record: RecordInstance | RecordModel): RecordModel => {
        if (record instanceof RecordModel) {
            if (!(this.rootStore.records as any).includes(record.record)) {
                this.rootStore.records.push(record.record);
            }
            return record;
        } else {
            if (!(this.rootStore.records as any).includes(record)) {
                this.rootStore.records.push(record);
            }
            return this.recordModels.find(recMod => recMod.recordId === record.recordId);
        }
    };

    /**
     * Handle get recordModel case where 1 or more records have been added (usually 1 record)
     */
    private handleAddedRecords = action(() => {
        // Iterate through the records, get only records not found in a recordModel
        const newRecords = this.rootStore.records.filter(
            record =>
                !this._recordModels.some(
                    recordModel => recordModel.record.recordId === record.recordId
                )
        );

        // If no new records, return existing record models
        if (!newRecords) return this._recordModels;

        // Build RecordModels from new Records, add them to recordModels collection & return it
        this.addRecordsToRecordModels(newRecords);
        return this._recordModels;
    });

    /**
     * Build RecordModels from new Records & add them to _recordModels collection
     * @mutates {RecordModel[]} _recordModels field (this._recordModels)
     */
    @action
    private addRecordsToRecordModels(newRecords: RecordInstance[]) {
        newRecords.forEach(record => this._recordModels.push(this.buildRecordModel(record)));
        this.rootStore.sortRecordsSetCurrent();
    }

    /**
     * Build a record model from the given record
     */
    private buildRecordModel = (record: RecordInstance) => new RecordModel(this.rootStore, record);

    /************ CURRENT ACTIVE RECORD ************/
    /**
     * Currently active record model (in this.recordModels)
     */
    @observable
    public activeRecordIdx: Int = null;

    /**
     * Set the currently active record to given index {number},
     * record model {RecordModel}, or record ID {string}
     *
     * @param {Int|string|RecordModel} recordIdOrIdxOrRecordModel Data to use to set active record:
     *        Int: Index of record in records array
     *        string: Record ID of record
     *        RecordModel: Actual record model wrapping the record
     * @return {void} Index of new active record model
     */
    @action
    public setActiveRecord = (
        recordIdOrIdxOrRecordModel: RecordId | Int | RecordModel
    ): void | never => {
        // Set using this.recordModels array index
        if (typeof recordIdOrIdxOrRecordModel === `number`) {
            if (this.recordModels[recordIdOrIdxOrRecordModel]) {
                this.activeRecordIdx = recordIdOrIdxOrRecordModel;
            }
            // If RecordModal not found at index, activate 1st RecordModel
            else if (this.recordModels.length > 0) {
                this.activeRecordIdx = 0;
            }
            // Throw if no RecordModels
            else {
                const errMsg =
                    `RecordModelsStore :: setActiveRecord: ` +
                    `No record at given index (${recordIdOrIdxOrRecordModel}) of ` +
                    `this.recordModels array - cannot set as active record model index`;
                console.error(errMsg);
                throw new Error(errMsg);
            }
            return;
        }

        // Set using record ID of RecordModel to set as active
        // If record ID not found, set the first RecordModel as active
        else if (typeof recordIdOrIdxOrRecordModel === `string`) {
            const idx = (this.activeRecordIdx = this.recordModels.findIndex(
                recMod => recMod.recordId === recordIdOrIdxOrRecordModel
            ));
            if (this.activeRecordIdx !== -1) {
                this.activeRecordIdx = idx;
            } else if (this.recordModels.length > 0) {
                this.activeRecordIdx = 0;
            }
        }

        // Set active RecordModel to provided RecordModel (throw if not found)
        else {
            const idx = this.recordModels.findIndex(
                recMod => recMod.recordId === recordIdOrIdxOrRecordModel.recordId
            );

            if (this.activeRecordIdx !== -1) {
                this.activeRecordIdx = idx;
            } else {
                console.error(
                    `RecordModelsStore#setActiveRecordIdx: No record found using recordModel/ID:`,
                    recordIdOrIdxOrRecordModel
                );
                throw new Error(
                    `No record found from: ${get(
                        recordIdOrIdxOrRecordModel as any,
                        'recordId',
                        recordIdOrIdxOrRecordModel
                    )}`
                );
            }
        }
    };

    /**
     * Currently activated RecordModal - i.e. record currently selected by user
     * @return {RecordModel} instance at [activeRecordIdx] position in [recordModels] array
     */
    // @computed
    public get activeRecordModel(): RecordModel | null {
        if (this.recordModels.length === 0) return null;
        return this.recordModels[this.activeRecordIdx];
    }

    /************ NOTIFICATIONS ************/
    /**
     * Get all notifications from all records, in a friendly, usable data object
     * TODO block from running until loading complete (determine if still needed)
     */
    public getNotifications = (): Array<{
        fullName: string;
        recordId: RecordId;
        notifications: Array<{title: string; status: number; obj: any}>;
    }> =>
        this.rootStore.modelInitStatusStore.isStoreInitialized
            ? this.notifications.map(({recordNotifications, fullName, recordId}) => ({
                  fullName,
                  recordId,
                  notifications: recordNotifications.map(({title, status, obj}) => ({
                      title,
                      status,
                      obj,
                  })),
              }))
            : [];

    /**
     * Get all notifications from all recordModels
     */
    @computed
    private get notifications(): Array<{
        recordNotifications: NotificationInstance[];
        fullName: string;
        recordId: string;
        fullNameQueryParam: string;
    }> {
        return this.recordModels.map(recordModel => ({
            recordNotifications: recordModel.getRecordNotifications(),
            fullName: recordModel.fullName,
            recordId: recordModel.recordId,
            fullNameQueryParam: recordModel.fullNameQueryParam,
        }));
    }

    /************ INSTANTIATION ************/
    private constructor(public rootStore: RootStore) {}

    public static new(rootStore: RootStore) {
        if (!RecordModelsStore.Instance) {
            RecordModelsStore.Instance = new RecordModelsStore(rootStore);
        }
        return RecordModelsStore.Instance;
    }

    /************ PRIVATE - HELPERS ************/
    /**
     * Handle get recordModel case where 1 or more records have been deleted (usually 1 record)
     */
    private handleDeletedRecords = action(() => {
        const recModels = this.rootStore.records.map(this.buildRecordModel);
        this.setRecordModels(recModels);
        return this._recordModels;
    });

    /**
     * Set recordModels' value by replacing it with the new given values
     * Mutates this._recordModels (RecordModel[])
     */
    @action
    private setRecordModels(recModels: RecordModel[]) {
        if (get(this, `_recordModels.length`) === 0 && isObservableArray(this._recordModels)) {
            // If _recordModels empty, push recModels onto it 1 by 1
            // Blocks err on last record deleleted
            recModels.forEach(action((recModel: RecordModel) => this._recordModels.push(recModel)));
        } else if (get(this, `_recordModels.length`) === 0 && recModels.length === 0) {
            // Don't replace nothing with nothing
            return;
        } else {
            this._recordModels.replace(recModels);
        }
    }
}
